import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { patientService } from '../services/patientService'
import { medicationService } from '../services/medicationService'
import { practiceLocationService } from '../services/practiceLocationService'
import { userService } from '../services/userService'
import { authService } from '../services/authService'
import { Patient } from '../types/patient'
import { PatientMedication } from '../types/medication'
import { PracticeLocation } from '../types/practiceLocation'
import { User } from '../types/user'
import './PatientMedicationsPrint.css'

export function PatientMedicationsPrint() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const {
    data: patient,
    isLoading: loadingPatient,
    error: patientError,
  } = useQuery<Patient>({
    queryKey: ['patient', id],
    queryFn: () => patientService.getById(id!),
    enabled: !!id,
  })

  const {
    data: medsResponse,
    isLoading: loadingMeds,
    error: medsError,
  } = useQuery<{ items: PatientMedication[]; totalCount: number; hasMore: boolean}>({
    queryKey: ['patient-medications', id, 'print'],
    queryFn: () => medicationService.getPatientMedications(id!, { status: undefined }),
    enabled: !!id,
  })

  const {
    data: practiceLocation,
    isLoading: loadingPractice,
    error: practiceError,
  } = useQuery<PracticeLocation>({
    queryKey: ['practice-location'],
    queryFn: () => practiceLocationService.getCurrent(),
  })

  const currentUser = authService.getCurrentUser()
  const {
    data: providerUser,
    isLoading: loadingProvider,
    error: providerError,
  } = useQuery<User | null>({
    queryKey: ['user', currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return null
      try {
        const users = await userService.getAll()
        return users.find((u) => u.id === currentUser.id) || null
      } catch {
        return null
      }
    },
    enabled: !!currentUser?.id,
  })

  const isLoading = loadingPatient || loadingMeds || loadingPractice || loadingProvider
  const error = patientError || medsError || practiceError || providerError

  useEffect(() => {
    // Optionally auto-open print dialog once loaded
    if (!isLoading && !error) {
      // Small timeout so layout can render
      const t = setTimeout(() => {
        window.print()
      }, 300)
      return () => clearTimeout(t)
    }
  }, [isLoading, error])

  if (!id) {
    return <div className="print-wrapper">Invalid patient.</div>
  }

  if (isLoading) {
    return <div className="print-wrapper">Preparing medication list...</div>
  }

  if (error || !patient || !medsResponse) {
    return <div className="print-wrapper">Unable to load medication list for printing.</div>
  }

  const currentMeds = medsResponse.items.filter((m) => m.status === 'active' && !m.isDoneInError)
  const pastMeds = medsResponse.items.filter((m) => m.status !== 'active' || m.isDoneInError)
  const totalCount = medsResponse.totalCount
  const visibleCount = medsResponse.items.length
  const hasOverflow = totalCount > visibleCount

  const chunk = <T,>(items: T[], size: number): T[][] => {
    if (size <= 0) return [items]
    const chunks: T[][] = []
    for (let i = 0; i < items.length; i += size) {
      chunks.push(items.slice(i, i + size))
    }
    return chunks.length ? chunks : [[]]
  }

  const currentPages = chunk(currentMeds, 12).map((items, index, all) => ({
    kind: 'current' as const,
    items,
    part: index + 1,
    totalParts: all.length,
  }))

  const pastPages = chunk(pastMeds, 14).map((items, index, all) => ({
    kind: 'past' as const,
    items,
    part: index + 1,
    totalParts: all.length,
  }))

  const pages = [
    ...(currentMeds.length ? currentPages : []),
    ...(pastMeds.length ? pastPages : []),
  ]
  const totalPages = pages.length || 1

  const primaryAddress = practiceLocation?.addresses?.find((address) => address.isActive && address.type === 'primary')
  const primaryContact = practiceLocation?.contacts?.find((contact) => contact.isPrimary)
  const formattedPatientAddress = patient.address
    ? [patient.address.street, patient.address.city, patient.address.state, patient.address.postalCode, patient.address.country]
        .filter(Boolean)
        .join(', ')
    : '-'
  const formattedPracticeAddress = primaryAddress
    ? [primaryAddress.addressLine1, primaryAddress.addressLine2, primaryAddress.city, primaryAddress.county, primaryAddress.postcode, primaryAddress.country]
        .filter(Boolean)
        .join(', ')
    : '-'
  const providerDisplayName = providerUser
    ? `${providerUser.salutation ? `${providerUser.salutation} ` : ''}${providerUser.firstName} ${providerUser.lastName}`.trim()
    : currentUser
      ? `${currentUser.firstName} ${currentUser.lastName}`.trim()
      : '-'
  const providerRole = providerUser?.role || currentUser?.role || '-'
  const providerHpi = providerUser?.hpi || '-'

  return (
    <div className="medications-print-page">
      <div className="print-toolbar no-print">
        <button className="btn-secondary" onClick={() => navigate(-1)}>
          Back
        </button>
        <button className="btn-primary" onClick={() => window.print()}>
          Print
        </button>
      </div>

      <div className="print-page">
        <header className="print-header">
          <h1>Medication List</h1>
          <div className="patient-block">
            <div>
              <strong>
                {patient.firstName} {patient.lastName}
              </strong>
            </div>
            <div className="patient-line">
              <span>DOB: {new Date(patient.dateOfBirth).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
              })}</span>
              <span>Patient ID: {patient.patientNumber || patient.id.slice(0, 8)}</span>
            </div>
          </div>
          <div className="meta-line">
            <span>Generated: {new Date().toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'long',
              year: 'numeric',
            })} at {new Date().toLocaleTimeString('en-GB', {
              hour: '2-digit',
              minute: '2-digit',
            })}</span>
          </div>
          {hasOverflow && (
            <div className="overflow-warning">
              This list may be truncated. Only {visibleCount} of {totalCount} medications are loaded.
            </div>
          )}
        </header>

        <section className="print-section">
          <h2>Patient details</h2>
          <div className="print-info-grid">
            <div className="print-info-card">
              <div className="print-info-title">Identifiers</div>
              <div className="print-info-row"><span>IHI</span><span>{patient.identifiersExtended?.ihiNumber || '-'}</span></div>
              <div className="print-info-row"><span>PPS</span><span>{patient.ppsNumber || patient.identifiersExtended?.ppsNumber || '-'}</span></div>
              <div className="print-info-row"><span>MPI</span><span>{patient.patientNumber || '-'}</span></div>
            </div>
            <div className="print-info-card">
              <div className="print-info-title">Contact</div>
              <div className="print-info-row"><span>Address</span><span>{formattedPatientAddress}</span></div>
              <div className="print-info-row"><span>Phone</span><span>{patient.phone || '-'}</span></div>
              <div className="print-info-row"><span>Email</span><span>{patient.email || '-'}</span></div>
            </div>
          </div>
        </section>

        <section className="print-section">
          <h2>Provider & clinic</h2>
          <div className="print-info-grid">
            <div className="print-info-card">
              <div className="print-info-title">Provider</div>
              <div className="print-info-row"><span>Name</span><span>{providerDisplayName}</span></div>
              <div className="print-info-row"><span>Role</span><span>{providerRole}</span></div>
              <div className="print-info-row"><span>HPI</span><span>{providerHpi}</span></div>
            </div>
            <div className="print-info-card">
              <div className="print-info-title">Clinic</div>
              <div className="print-info-row"><span>Name</span><span>{practiceLocation?.practiceName || '-'}</span></div>
              <div className="print-info-row"><span>Address</span><span>{formattedPracticeAddress}</span></div>
              <div className="print-info-row"><span>Phone</span><span>{primaryContact?.value || '-'}</span></div>
            </div>
          </div>
        </section>
      </div>

      {(pages.length ? pages : [{ kind: 'current', items: [], part: 1, totalParts: 1 }]).map((page, index) => {
        const isLastPage = index === totalPages - 1
        const title =
          page.kind === 'current'
            ? page.part > 1
              ? 'Current Medications (continued)'
              : 'Current Medications'
            : page.part > 1
              ? 'Past / Error Medications (continued)'
              : 'Past / Error Medications'

        return (
          <div
            key={`${page.kind}-${page.part}`}
            className="print-page page-break-before"
          >
            {page.kind === 'current' && (
              <section className="print-section">
                <h2>{title}</h2>
                {page.items.length === 0 ? (
                  <p className="muted">No active medications recorded.</p>
                ) : (
                  <table className="print-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Dose & Route</th>
                        <th>Frequency</th>
                        <th>Quantity</th>
                        <th>Indication</th>
                        <th>Prescriber</th>
                        <th>Start</th>
                      </tr>
                    </thead>
                    <tbody>
                      {page.items.map((m) => (
                        <tr key={m.medicationId}>
                          <td>{m.name}</td>
                          <td>
                            {m.doseText || (m.dose ? `${m.dose.value ?? ''} ${m.dose.unit ?? ''}`.trim() : '')}
                            {m.route && <span className="route"> / {m.route}</span>}
                          </td>
                          <td>{m.frequency?.text || m.frequency?.code || '-'}</td>
                          <td>
                            {m.quantity
                              ? `${m.quantity.value ?? ''} ${m.quantity.unit ?? ''}`.trim()
                              : '-'}
                          </td>
                          <td>{m.indication || '-'}</td>
                          <td>{m.prescriberName || '-'}</td>
                          <td>
                            {m.startDate
                              ? new Date(m.startDate).toLocaleDateString('en-GB', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                })
                              : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
            )}

            {page.kind === 'past' && (
              <section className="print-section">
                <h2>{title}</h2>
                <table className="print-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Dates</th>
                      <th>Prescriber</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {page.items.map((m) => (
                      <tr key={m.medicationId}>
                        <td>{m.name}</td>
                        <td>
                          {m.isDoneInError ? 'Done in error' : (m.status ? m.status.charAt(0).toUpperCase() + m.status.slice(1) : '-')}
                        </td>
                        <td>
                          {m.startDate
                            ? new Date(m.startDate).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                              }).replace(/\s/g, ' ')
                            : '-'}
                          {m.endDate && ` / ${new Date(m.endDate).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          })}`}
                        </td>
                        <td>{m.prescriberName || '-'}</td>
                        <td>{m.isDoneInError && m.errorReason ? m.errorReason : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}

            {!isLastPage && (
              <div className="print-continues-note">
                Continued on next page.
              </div>
            )}
          </div>
        )
      })}

      {hasOverflow && (
        <footer className="print-footer">
          <p>
            Note: Additional medications not shown here may exist in the electronic record. Refer to the
            system for the complete list.
          </p>
        </footer>
      )}
    </div>
  )
}
